import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useGesture } from "@use-gesture/react";
import {
  DEFAULT_ZOOM_LIMITS,
  IDENTITY_VIEW,
  computePaintedRect,
  edgePushDirection,
  isZoomed as isViewZoomed,
  panByClamped,
  screenToImagePoint,
  toCssTransform,
  zoomAt,
  type EdgeDirection,
  type ImagePoint,
  type ObjectFit,
  type Point,
  type Size,
  type ZoomLimits,
  type ZoomView,
} from "@client/lib/foscam/digitalZoom";

/**
 * Digitální zoom obrazu kamery — napojení čisté matematiky (`lib/foscam/digitalZoom.ts`)
 * na gesta, rAF a DOM.
 *
 * Tři věci, na kterých to stojí:
 *
 *  1. **Transform se zapisuje imperativně do `<video>`, ne přes React prop.**
 *     `CameraStream` aplikuje `style` prop jen v mount efektu závislém na `[slug]`, takže
 *     měnící se `style.transform` by po prvním renderu tiše přestal fungovat. Navíc by
 *     to znamenalo re-render na každý tik kolečka.
 *  2. **Živá hodnota je v refu, ve stavu je jen to, co kreslí UI** (jestli je
 *     zazoomováno a zaokrouhlené měřítko do indikátoru). Díky tomu je při gestu
 *     nejvýš jeden zápis do DOM na snímek a re-render jen při viditelné změně.
 *  3. **`target: frameRef` + `eventOptions: { passive: false }`** je povinné, ne kosmetika:
 *     React 19 registruje `wheel`/`touchmove` na rootu jako passive, takže
 *     `preventDefault()` v syntetickém handleru tiše selže. Stejný důvod jako
 *     v `Graph2Interaction.ts`.
 */

/** Práh, nad kterým se z tapu stane tažení. Sladěno s Graph2 (Android touch-slop). */
const DRAG_THRESHOLD_PX = 8;
/** Okno pro dvojklik / double-tap. */
const DOUBLE_TAP_GAP_MS = 300;
const DOUBLE_TAP_DIST_PX = 24;
/** Citlivost kolečka. Exponenciála zvládne jak myš (velké skoky), tak trackpad. */
const WHEEL_SENSITIVITY = 0.0015;
/** Kolik smí jeden wheel event změnit měřítko — pojistka proti divokým deltám. */
const WHEEL_FACTOR_CLAMP = 2;
/**
 * Jak dlouho musí tažení souvisle přetahovat přes okraj, než se pohne kamera.
 * Bez toho by každé škubnutí u okraje poslalo PTZ příkaz.
 */
const EDGE_PUSH_SUSTAIN_MS = 220;
/**
 * Rozestup opakovaných edge-push příkazů. Musí být řádově nad dojezdem obrazu, jinak
 * uživatel reaguje na to, co už neplatí, a kamera ujede.
 */
const EDGE_PUSH_REPEAT_MS = 600;

export interface UseDigitalZoomOptions {
  frameRef: RefObject<HTMLDivElement | null>;
  /** Ref na `<video>`; vlastní ho dlaždice a předává ho `CameraStream`. */
  videoRef: RefObject<HTMLVideoElement | null>;
  /**
   * Mění se, když `CameraStream` dostane nový `key` (restart streamu, jiná kamera).
   * Je to jediný spolehlivý signál, že v `videoRef` je jiný element — nový obraz
   * znamená zahodit výřez, protože zděděný by mířil kdovíkam.
   */
  videoKey: string;
  /** `contain` ve fill režimech, `none` v mřížce. Odvozuje se ze stejného `fill`. */
  fit: ObjectFit;
  /** Vypnuto v editoru layoutu — binder pak nesmí ani volat `preventDefault`. */
  enabled: boolean;
  /** Tažení za okraj výřezu → fyzické otočení kamery. Bez toho tažení skončí na okraji. */
  onEdgePush?: (direction: EdgeDirection) => void;
  /**
   * Dvojklik do obrazu. Dostane bod v normalizovaných souřadnicích OBRAZU (pro kameru)
   * i v px lokálně vůči rámu (pro značku v UI). Vrátí `true`, když si ho vzal —
   * jinak se dvojklik použije na reset zoomu.
   */
  onDoubleTap?: (point: ImagePoint, local: Point) => boolean;
  limits?: ZoomLimits;
}

export interface UseDigitalZoomApi {
  isZoomed: boolean;
  /** Zaokrouhlené měřítko do indikátoru (mění se po desetinách, ne po pixelech). */
  scale: number;
  resetZoom: () => void;
  /** Krok zoomu ze středu obrazu — pro tlačítka (a tím i pro klávesnici). */
  zoomBy: (factor: number) => void;
  /** Přečti a zahoď: skončilo poslední gesto tažením? Brání falešnému kliku. */
  consumeDidDrag: () => boolean;
}

export function useDigitalZoom(options: UseDigitalZoomOptions): UseDigitalZoomApi {
  const { frameRef, videoRef, videoKey, fit, enabled, onEdgePush, onDoubleTap, limits = DEFAULT_ZOOM_LIMITS } =
    options;

  const viewRef = useRef<ZoomView>(IDENTITY_VIEW);
  const [isZoomed, setIsZoomed] = useState(false);
  const [scale, setScale] = useState(1);
  const isZoomedRef = useRef(false);
  const scaleRef = useRef(1);

  const dragStartViewRef = useRef<ZoomView>(IDENTITY_VIEW);
  const didDragRef = useRef(false);
  const pendingPaintRef = useRef(false);
  const lastTapRef = useRef<{ t: number; x: number; y: number } | null>(null);
  const overflowSinceRef = useRef<number | null>(null);
  const lastEdgeFireRef = useRef(0);

  /**
   * Aktuální hodnoty pro handlery. Díky refům můžou být callbacky stabilní a
   * `useGesture` se nemusí převazovat při každém renderu rodiče.
   *
   * Zápis v efektu, ne v renderu (stejně jako `latencyRef` v `usePtzHold`): efekty se
   * propíšou po commitu, tedy dřív, než uživatel stihne cokoli udělat.
   */
  const fitRef = useRef(fit);
  const enabledRef = useRef(enabled);
  const limitsRef = useRef(limits);
  const onEdgePushRef = useRef(onEdgePush);
  const onDoubleTapRef = useRef(onDoubleTap);
  useEffect(() => {
    fitRef.current = fit;
    enabledRef.current = enabled;
    limitsRef.current = limits;
    onEdgePushRef.current = onEdgePush;
    onDoubleTapRef.current = onDoubleTap;
  }, [fit, enabled, limits, onEdgePush, onDoubleTap]);

  /** Geometrie rámu + namalovaného obrazu. `null` = ještě není co počítat. */
  const readGeometry = useCallback((): { container: Size; painted: ReturnType<typeof computePaintedRect> } | null => {
    const frame = frameRef.current;
    const video = videoRef.current;
    if (!frame || !video) return null;
    const rect = frame.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const container: Size = { width: rect.width, height: rect.height };
    return {
      container,
      painted: computePaintedRect(container, { width: video.videoWidth, height: video.videoHeight }, fitRef.current),
    };
  }, [frameRef, videoRef]);

  const requestPaint = useCallback(() => {
    if (pendingPaintRef.current) return;
    pendingPaintRef.current = true;
    requestAnimationFrame(() => {
      pendingPaintRef.current = false;
      const video = videoRef.current;
      const geo = readGeometry();
      if (!video || !geo) return;
      const css = toCssTransform(viewRef.current, geo.container, geo.painted);
      video.style.transform = css.transform;
      video.style.transformOrigin = css.transformOrigin;
    });
  }, [readGeometry, videoRef]);

  /** Jediné místo, kde se mění pohled. Drží ref, stav i překreslení v souladu. */
  const applyView = useCallback(
    (next: ZoomView) => {
      viewRef.current = next;
      const zoomedNow = isViewZoomed(next);
      if (zoomedNow !== isZoomedRef.current) {
        isZoomedRef.current = zoomedNow;
        setIsZoomed(zoomedNow);
      }
      const rounded = Math.round(next.scale * 10) / 10;
      if (rounded !== scaleRef.current) {
        scaleRef.current = rounded;
        setScale(rounded);
      }
      requestPaint();
    },
    [requestPaint]
  );

  const resetZoom = useCallback(() => {
    applyView(IDENTITY_VIEW);
  }, [applyView]);

  const zoomBy = useCallback(
    (factor: number) => {
      const geo = readGeometry();
      if (!geo) return;
      const centre: Point = { x: geo.container.width / 2, y: geo.container.height / 2 };
      applyView(zoomAt(viewRef.current, geo.container, geo.painted, centre, factor, limitsRef.current));
    },
    [applyView, readGeometry]
  );

  const consumeDidDrag = useCallback(() => {
    const v = didDragRef.current;
    didDragRef.current = false;
    return v;
  }, []);

  /**
   * Nový `<video>` (ruční restart streamu): navázat listenery a překreslit.
   *
   * Výřez se schválně NERESETUJE. Klíč se v rámci jedné dlaždice mění jen při ručním
   * restartu streamu téže kamery — míří pořád na totéž místo, takže zahodit přiblížení
   * by uživatele akorát vrátilo na začátek. Změna kamery tudy vůbec neprojde: dlaždice
   * má v `Foscams` `key` podle jména kamery, takže se rovnou přemountuje celá.
   *
   * Efekt smí spoléhat na to, že `videoRef` už je naplněný — efekty potomků běží před
   * efekty rodiče a `CameraStream` element vytváří synchronně ve svém mount efektu
   * (je to tam i explicitně okomentované).
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // Rozměry obrazu dorazí až s metadaty; do té doby je `painted` jen celý rám.
    // `resize` na media elementu = změna videoWidth/videoHeight BEZ výměny elementu
    // (přepnutí kodeku / profilu). Výřez je normalizovaný, takže stačí překreslit.
    const onMeta = (): void => requestPaint();
    video.addEventListener("loadedmetadata", onMeta);
    video.addEventListener("resize", onMeta);
    requestPaint();
    return () => {
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("resize", onMeta);
    };
  }, [videoKey, videoRef, requestPaint]);

  /**
   * Vlastní ResizeObserver, ne sdílený `useResizeObserver` — ten má 250ms debounce
   * a 50px kvantizaci kvůli přenačítání grafů. Tady by to znamenalo, že po otočení
   * telefonu obraz čtvrt sekundy sedí špatně.
   */
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const ro = new ResizeObserver(() => requestPaint());
    ro.observe(frame);
    return () => ro.disconnect();
  }, [frameRef, requestPaint]);

  /** Změna režimu dlaždice mění `object-fit`, a tím i namalovaný obdélník. */
  useEffect(() => {
    requestPaint();
  }, [fit, requestPaint]);

  /** Ochrana: když se gesta vypnou (editor layoutu), nesmí zůstat rozjetý edge-push. */
  useEffect(() => {
    if (!enabled) overflowSinceRef.current = null;
  }, [enabled]);

  const handleEdgeOverflow = useCallback((overflowPx: Point, last: boolean) => {
    const push = onEdgePushRef.current;
    if (!push) return;
    const direction = edgePushDirection(viewRef.current, overflowPx);
    if (!direction || last) {
      overflowSinceRef.current = null;
      return;
    }
    const now = performance.now();
    if (overflowSinceRef.current === null) {
      overflowSinceRef.current = now;
      return;
    }
    if (now - overflowSinceRef.current < EDGE_PUSH_SUSTAIN_MS) return;
    if (now - lastEdgeFireRef.current < EDGE_PUSH_REPEAT_MS) return;
    lastEdgeFireRef.current = now;
    push(direction);
  }, []);

  const toLocalPoint = useCallback(
    (clientX: number, clientY: number): Point | null => {
      const frame = frameRef.current;
      if (!frame) return null;
      const rect = frame.getBoundingClientRect();
      return { x: clientX - rect.left, y: clientY - rect.top };
    },
    [frameRef]
  );

  useGesture(
    {
      onDrag: (state) => {
        if (!enabledRef.current) {
          state.cancel();
          return;
        }
        const { first, last, movement, pinching, xy, tap } = state;
        if (pinching) {
          state.cancel();
          return;
        }
        if (first) {
          dragStartViewRef.current = viewRef.current;
          didDragRef.current = false;
          overflowSinceRef.current = null;
        }
        const dist = Math.hypot(movement[0], movement[1]);
        if (dist > DRAG_THRESHOLD_PX) didDragRef.current = true;

        const geo = readGeometry();
        if (geo && isViewZoomed(dragStartViewRef.current)) {
          const { view, overflowPx } = panByClamped(
            dragStartViewRef.current,
            geo.container,
            geo.painted,
            movement[0],
            movement[1],
            limitsRef.current
          );
          applyView(view);
          handleEdgeOverflow(overflowPx, last);
        }

        if (last) {
          overflowSinceRef.current = null;
          if (tap || dist <= DRAG_THRESHOLD_PX) handleTap(xy[0], xy[1]);
        }
      },

      onPinch: (state) => {
        if (!enabledRef.current) {
          state.cancel();
          return;
        }
        const { origin, da, first, memo } = state;
        // Gesto s dvěma prsty nikdy nesmí skončit jako tap — jinak by pinch v mřížce
        // otevřel spotlight. `didDrag` je zároveň guard pro klik.
        didDragRef.current = true;
        const geo = readGeometry();
        if (!geo) return memo;
        let m = memo as { startView: ZoomView; startDist: number } | undefined;
        if (first || !m || !m.startDist) m = { startView: viewRef.current, startDist: da[0] || 1 };
        const factor = (da[0] || m.startDist) / m.startDist;
        if (!Number.isFinite(factor) || factor <= 0) return m;
        const local = toLocalPoint(origin[0], origin[1]);
        if (!local) return m;
        applyView(zoomAt(m.startView, geo.container, geo.painted, local, factor, limitsRef.current));
        return m;
      },

      onWheel: (state) => {
        if (!enabledRef.current) return;
        const event = state.event;
        const geo = readGeometry();
        if (!geo) return;
        const local = toLocalPoint(event.clientX, event.clientY);
        if (!local) return;
        const raw = Math.exp(-event.deltaY * WHEEL_SENSITIVITY);
        const factor = Math.min(WHEEL_FACTOR_CLAMP, Math.max(1 / WHEEL_FACTOR_CLAMP, raw));
        const next = zoomAt(viewRef.current, geo.container, geo.painted, local, factor, limitsRef.current);
        // preventDefault JEN když se opravdu zoomuje. Na `scale = 1` a odzoomování se
        // událost pustí dál, takže se stránka pod kamerou dá normálně scrollovat.
        if (next.scale === viewRef.current.scale) return;
        event.preventDefault();
        applyView(next);
      },
    },
    {
      target: frameRef,
      eventOptions: { passive: false },
      drag: { filterTaps: true, threshold: 0 },
    }
  );

  /** Dvojklik: u PTZ kamer míření, jinak (a když si ho míření nevezme) reset zoomu. */
  function handleTap(clientX: number, clientY: number): void {
    const now = performance.now();
    const prev = lastTapRef.current;
    const isDouble =
      prev !== null && now - prev.t < DOUBLE_TAP_GAP_MS && Math.hypot(clientX - prev.x, clientY - prev.y) < DOUBLE_TAP_DIST_PX;
    if (!isDouble) {
      lastTapRef.current = { t: now, x: clientX, y: clientY };
      return;
    }
    lastTapRef.current = null;
    // Dvojklik nesmí propadnout jako klik na celodlaždicové tlačítko spotlightu.
    didDragRef.current = true;
    const geo = readGeometry();
    const local = toLocalPoint(clientX, clientY);
    const handler = onDoubleTapRef.current;
    if (geo && local && handler) {
      const imagePoint = screenToImagePoint(viewRef.current, geo.container, geo.painted, local);
      // Klik do letterboxu (mimo obraz) není platný cíl.
      const inside = imagePoint.u >= 0 && imagePoint.u <= 1 && imagePoint.v >= 0 && imagePoint.v <= 1;
      if (inside && handler(imagePoint, local)) return;
    }
    resetZoom();
  }

  return useMemo(
    () => ({ isZoomed, scale, resetZoom, zoomBy, consumeDidDrag }),
    [isZoomed, scale, resetZoom, zoomBy, consumeDidDrag]
  );
}
